import type { QuickFindShortcut } from '@erp/rpc';

export type QuickSearchPage = {
  id: string;
  label: string;
  sublabel?: string;
  path: string;
  kind: 'page' | 'setting';
  /** Lowercased search-only aliases (e.g. "wf"), from Quick Find shortcuts. */
  keywords?: string[];
};

export function parseKeywords(value: string): string[] {
  return value
    .split(',')
    .map((k) => k.trim().toLowerCase())
    .filter((k) => k.length > 0);
}

/**
 * Expands `[keywords, path]` templates into one shortcut per keyword.
 * `keywords` is the already-translated, comma-separated string.
 */
export function expandShortcutTemplates(
  templates: readonly { keywords: string; path: string }[],
): QuickFindShortcut[] {
  return templates.flatMap(({ keywords, path }) =>
    parseKeywords(keywords).map((shortcut) => ({ shortcut, path })),
  );
}

/**
 * Attaches shortcuts to the pages they point at. Shortcuts whose path isn't
 * in `pages` are dropped — the catalog is already access-filtered, so a
 * shortcut to a page the user can't open never surfaces.
 */
export function attachShortcuts(
  pages: readonly QuickSearchPage[],
  shortcuts: readonly QuickFindShortcut[],
): QuickSearchPage[] {
  const byPath = new Map<string, string[]>();
  for (const { shortcut, path } of shortcuts) {
    byPath.set(path, [...(byPath.get(path) ?? []), shortcut]);
  }
  return pages.map((p) => {
    const keywords = byPath.get(p.path);
    return keywords ? { ...p, keywords } : p;
  });
}

/**
 * Substring match on label/sublabel, plus prefix match on keywords. Pages hit
 * by an exact keyword ("wf") sort first so the shortcut lands on top.
 */
export function filterQuickSearchPages(
  pages: readonly QuickSearchPage[],
  search: string,
): QuickSearchPage[] {
  const q = search.trim().toLowerCase();
  if (!q) return [];

  const exact: QuickSearchPage[] = [];
  const rest: QuickSearchPage[] = [];
  for (const p of pages) {
    const keywords = p.keywords ?? [];
    if (keywords.includes(q)) {
      exact.push(p);
    } else if (
      p.label.toLowerCase().includes(q) ||
      (p.sublabel?.toLowerCase().includes(q) ?? false) ||
      keywords.some((k) => k.startsWith(q))
    ) {
      rest.push(p);
    }
  }
  return [...exact, ...rest];
}
